export interface OiAnalysisOrderDTO {
  uuid: string;
  jsonData: string;
  createdAt?: string;
  lastUpdated?: string;
  orderId?: string;
  isDraft?: boolean;
  isArchived?: boolean;
  isCancelled?: boolean;
  isSubmittedForReview?: boolean;
  reviewPassed?: boolean | null;
  author?: string;
}

export interface OiAnalysisOrderDTOMinimal {
  uuid: string;
  orderId?: string;
}

export interface OiAnalysisOrderGrouped {
  drafts: OiAnalysisOrderDTOMinimal[];
  readyForReview: OiAnalysisOrderDTOMinimal[];
  reviewed: OiAnalysisOrderDTOMinimal[];
  failedReview: OiAnalysisOrderDTOMinimal[];
  cancelled: OiAnalysisOrderDTOMinimal[];
}

const BASE_URL = `${process.env.REACT_APP_API_URL}/oi-analysis-order`;

export async function CreateOiAnalysisOrder(
  payload: string,
  isDraft: boolean,
  author: string,
  isSubmittedForReview = false,
  reviewPassed: boolean | null = null,
): Promise<string | null> {
  try {
    const response = await fetch(`${BASE_URL}/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        JsonData: payload,
        IsDraft: isDraft,
        Author: author,
        IsSubmittedForReview: isSubmittedForReview,
        ReviewPassed: reviewPassed,
      }),
    });

    if (response.ok) {
      return await response.text();
    }

    return null;
  } catch (error) {
    console.error("Failed to create OiAnalysisOrder", error);
    return null;
  }
}

export async function UpdateOiAnalysisOrder(
  uuid: string,
  payload: string,
  isDraft: boolean,
  isSubmittedForReview = false,
  reviewPassed: boolean | null = null,
): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/${uuid}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        JsonData: payload,
        IsDraft: isDraft,
        IsSubmittedForReview: isSubmittedForReview,
        ReviewPassed: reviewPassed,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error("Failed to update OiAnalysisOrder", error);
    return false;
  }
}

export async function GetOiAnalysisOrder(uuid: string): Promise<OiAnalysisOrderDTO | null> {
  try {
    const response = await fetch(`${BASE_URL}/${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch OiAnalysisOrder", error);
    return null;
  }
}

export async function getAllOiAnalysisOrdersGrouped(): Promise<OiAnalysisOrderGrouped | null> {
  try {
    const response = await fetch(`${BASE_URL}/all-grouped`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch grouped OiAnalysisOrders", error);
    return null;
  }
}

export async function getAnalysisOrdersFiltered(query: string): Promise<OiAnalysisOrderGrouped | null> {
  try {
    const response = await fetch(`${BASE_URL}/all-filtered?search=${encodeURIComponent(query)}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch filtered OiAnalysisOrders", error);
    return null;
  }
}

export async function cancelOiAnalysisOrder(uuid: string): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/${uuid}/cancel`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.ok;
  } catch (error) {
    console.error("Failed to cancel OiAnalysisOrder", error);
    return false;
  }
}

export async function deleteOiAnalysisOrder(uuid: string): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/${uuid}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.ok;
  } catch (error) {
    console.error("Failed to delete OiAnalysisOrder", error);
    return false;
  }
}

// The create form waits for the default staff before it is shown, so a hung request must not keep it loading.
const DEFAULT_TAGGED_STAFF_TIMEOUT_MS = 15000;

export async function GetDefaultTaggedStaff(timeoutMs: number = DEFAULT_TAGGED_STAFF_TIMEOUT_MS): Promise<string[]> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<string[]>((resolve) => {
    timer = setTimeout(() => {
      console.warn("Timed out loading the default tagged staff; continuing without them.");
      resolve([]);
    }, timeoutMs);
  });

  try {
    return await Promise.race([fetchDefaultTaggedStaff(), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

async function fetchDefaultTaggedStaff(): Promise<string[]> {
  try {
    const response = await fetch(`${BASE_URL}/default-tagged-staff`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const staff = await response.json();
      return Array.isArray(staff) ? staff.filter((email): email is string => typeof email === "string") : [];
    }

    return [];
  } catch (error) {
    console.error("Failed to fetch default tagged staff", error);
    return [];
  }
}